import type React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import SmartLinkIcon from '@atlaskit/icon/core/smart-link';

import { Ati } from '@atlassian/ari';

import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';

import type { UseMenuItemProps, UseMenuItemReturnProps } from '../index.tsx';

import { buildCreationLink } from '../../../CreateContentRouteComponent/index.ts';
import { useHybridLinkClick } from '../../../CreateContentPopover/CreateContentPopoverItems/hybridLinkHelper.ts';

const messages = defineMessages({
	title: {
		id: 'create-blank-fabric-page.universal-create.smart-link-embed.title',
		defaultMessage: 'Smart Link',
		description: 'title indicating that this option will create a whiteboard',
	},
});

export function useEmbedItem({
	parentId,
	source,
	spaceKey,
	onClick,
}: UseMenuItemProps): UseMenuItemReturnProps {
	const { formatMessage } = useIntl();
	const { enforceStorageLimit, shouldBlockCreate } = useHardStorageEnforcement({
		source: 'create-smart-link-embed-popover-item',
	});

	const createContentOptions = {
		contentType: 'smartLinkEmbed',
		parentPageId: parentId,
		withFallback: true,
		source,
		spaceKey,
	} as const;

	const createHref = buildCreationLink(createContentOptions);

	const hybridPageLinkClick = useHybridLinkClick(createHref);

	const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
		onClick('createSmartLinkEmbedPopoverItem');
		hybridPageLinkClick(e);
	};

	return {
		ati: Ati.parse('ati:cloud:confluence:embed'),
		children: formatMessage(messages.title),
		onClick: enforceStorageLimit(handleClick),
		elemBefore: SmartLinkIcon,
		...(!shouldBlockCreate && { href: createHref }),
		testId: `create-menu-${shouldBlockCreate ? 'button' : 'link'}-item`,
	};
}
